import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, shortDate, dateParams, showMoney } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import DateRangeFilter from "@/components/DateRangeFilter";
import { SyncStatus } from "@/components/SyncStatus";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FitBadge, FIT } from "@/components/FitBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { RefreshCw, Search, Loader2, AlertTriangle, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, Ruler } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const PAY_BADGE = {
  pagato: "border-emerald-500/20 bg-emerald-500/10 text-emerald-400",
  in_attesa: "border-amber-500/20 bg-amber-500/10 text-amber-400",
  annullato: "border-rose-500/20 bg-rose-500/10 text-rose-400",
};
const PAY_LABEL = {
  pagato: "Pagato", in_attesa: "In attesa di pagamento", annullato: "Annullato",
};
const FUL_BADGE = {
  spedito: "border-emerald-500/20 bg-emerald-500/10 text-emerald-400",
  in_preparazione: "border-amber-500/20 bg-amber-500/10 text-amber-400",
  in_attesa: "border-blue-500/20 bg-blue-500/10 text-blue-400",
  annullato: "border-rose-500/20 bg-rose-500/10 text-rose-400",
};
const FUL_LABEL = {
  spedito: "Spedito", in_preparazione: "In preparazione", in_attesa: "In attesa", annullato: "Annullato",
};
const FILTERS = [
  { id: "tutti", label: "Tutti" },
  { id: "in_attesa", label: "In attesa" },
  { id: "in_preparazione", label: "In preparazione" },
  { id: "spedito", label: "Spediti" },
  { id: "annullato", label: "Annullati" },
];

const SortHead = ({ label, col, sort, onSort, right }) => {
  const active = sort.by === col;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={right ? "text-right" : ""}>
      <button onClick={() => onSort(col)} data-testid={`sort-${col}`}
        className={`inline-flex items-center gap-1 transition-colors hover:text-foreground ${active ? "text-foreground" : ""}`}>
        {label}<Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
};

export default function Ordini() {
  const [filter, setFilter] = useState("tutti");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  const [range, setRange] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ by: "date", dir: "desc" });
  const toggleSort = (by) => {
    setSort((s) => (s.by === by ? { by, dir: s.dir === "asc" ? "desc" : "asc" } : { by, dir: by === "customer" ? "asc" : "desc" }));
    setPage(1);
  };
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    // Apertura dalla scheda cliente: /ordini?ordine=<numero>
    const n = new URLSearchParams(window.location.search).get("ordine");
    if (n) {
      api.get(`/orders/${encodeURIComponent(n)}`).then((r) => setSelected(r.data)).catch(() => toast.error("Ordine non trovato"));
    }
  }, []);

  const { data: res, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["orders", range, filter, q, page, sort],
    queryFn: async () => (await api.get("/orders", { params: { ...dateParams(range), status: filter, q: q || undefined, page, page_size: 50, sort: sort.by, direction: sort.dir } })).data,
    placeholderData: keepPreviousData,
  });
  const orders = res?.items || [];
  const { data: detail } = useQuery({
    queryKey: ["order", selected?.id],
    queryFn: async () => (await api.get(`/orders/${selected.id}`)).data,
    enabled: !!selected,
  });

  const qc = useQueryClient();
  const sync = useMutation({
    mutationFn: async (full) => (await api.post("/orders/sync", null, { params: { full: !!full } })).data,
    onSuccess: (d) => {
      if (d.status === "started") {
        toast.success(d.full ? "Sincronizzazione completa avviata (circa 1 minuto)…" : "Aggiornamento ordini degli ultimi 7 giorni avviato…");
        qc.invalidateQueries({ queryKey: ["sync-status"] });
        setTimeout(() => qc.invalidateQueries({ queryKey: ["sync-status"] }), 1500);
        setTimeout(() => refetch(), 8000);
        setTimeout(() => refetch(), 20000);
        setTimeout(() => refetch(), 40000);
      } else {
        refetch();
        toast.info(`Nessun negozio Shopify collegato · ${d.synced} ordini demo. Collega Shopify da Prodotti.`);
      }
    },
  });

  const { data: syncStatus } = useQuery({
    queryKey: ["sync-status"],
    queryFn: async () => (await api.get("/shopify/sync-status")).data,
    refetchInterval: (q) => {
      const dd = q.state.data;
      return dd?.products?.running || dd?.orders?.running ? 3000 : 20000;
    },
  });
  const ordRunning = !!syncStatus?.orders?.running;
  const { data: shopCfg } = useQuery({
    queryKey: ["shopify-config"],
    queryFn: async () => (await api.get("/shopify/config")).data,
  });
  const limited = shopCfg?.connected && !shopCfg?.all_orders_access;


  return (
    <div>
      <PageHeader title="Ordini Shopify" subtitle="Tutti gli ordini ricevuti dal tuo negozio online.">
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <DateRangeFilter range={range} onChange={(r) => { setRange(r); setPage(1); }} testid="ordini-date-filter" />
            <div className="flex">
              <Button onClick={() => sync.mutate(false)} disabled={sync.isPending || ordRunning} className="rounded-r-none" data-testid="sync-shopify-btn">
                {(sync.isPending || ordRunning) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Aggiorna ordini recenti
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button disabled={sync.isPending || ordRunning} className="rounded-l-none border-l border-primary-foreground/20 px-2" data-testid="sync-options-btn">
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => sync.mutate(false)} data-testid="sync-recent-item">Ordini recenti (ultimi 7 giorni)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => sync.mutate(true)} data-testid="sync-full-item">Sincronizzazione completa dal 1° gennaio 2026</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <SyncStatus state={syncStatus?.orders} testid="sync-status-orders" />
        </div>
      </PageHeader>

      {limited && (
        <div className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm" data-testid="orders-limited-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <span>
            <strong>Storico ordini limitato a 60 giorni.</strong> Shopify non concede il permesso <code>read_all_orders</code>: gli ordini più vecchi non vengono importati né aggiornati (resi, rimborsi).
            Richiedi &quot;Read all orders&quot; nella tua app Shopify (Partner Dashboard → API access), poi ricollega il negozio da Prodotti → Shopify: l'import dal 1° gennaio 2026 partirà automaticamente.
          </span>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cerca cliente, n° ordine o codice prodotto (es. 8001 a26 nero)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 sm:w-[480px]"
            data-testid="order-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => { setFilter(f.id); setPage(1); }}
              data-testid={`order-filter-${f.id}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filter === f.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex h-60 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Ordine</TableHead>
                <SortHead label="Data" col="date" sort={sort} onSort={toggleSort} />
                <SortHead label="Cliente" col="customer" sort={sort} onSort={toggleSort} />
                <TableHead>Capi</TableHead>
                <TableHead>Pagamento</TableHead>
                <TableHead>Spedizione</TableHead>
                {showMoney() && <SortHead label="Totale" col="total" sort={sort} onSort={toggleSort} right />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} onClick={() => setSelected(o)} className="cursor-pointer" data-testid={`order-row-${o.order_number}`}>
                  <TableCell className="font-mono-erp text-sm font-medium">{o.order_number}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{shortDate(o.date)}</TableCell>
                  <TableCell className="text-sm">
                    {o.customer_id || o.customer_email ? (
                      <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/clienti?cliente=${encodeURIComponent(o.customer_id || o.customer_email)}`); }}
                        className="font-medium underline-offset-4 transition-colors hover:text-primary hover:underline" data-testid={`order-customer-link-${o.order_number}`}>{o.customer_name}</button>
                    ) : o.customer_name}
                    {o.first_order && o.channel === "online" && <Badge variant="outline" className="ml-2 border-primary/50 text-[10px] text-primary" data-testid={`order-first-${o.id}`}>Primo ordine</Badge>}
                    {o.fit_status && o.fit_status !== "ok" && <Badge variant="outline" className={`ml-2 text-[10px] ${FIT[o.fit_status].cls}`} data-testid={`order-fit-${o.id}`}><Ruler className="mr-1 h-3 w-3" />{FIT[o.fit_status].label}</Badge>}
                    {o.fit_status === "ok" && <Ruler className="ml-2 inline h-3.5 w-3.5 text-emerald-400" data-testid={`order-fit-${o.id}`} />}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{o.pieces} pz</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={PAY_BADGE[o.payment_status] || ""}>{PAY_LABEL[o.payment_status] || o.payment_status}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={FUL_BADGE[o.fulfillment_status] || ""}>{FUL_LABEL[o.fulfillment_status]}</Badge>
                  </TableCell>
                  {showMoney() && <TableCell className="text-right font-semibold">{eur(o.total)}</TableCell>}
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground">Nessun ordine trovato.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      {res && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground" data-testid="orders-pagination">
          <span data-testid="orders-total">
            {num(res.total)} ordini · pagina {res.page} di {res.pages}
            {isFetching && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin" />}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} data-testid="orders-first-page">Prima</Button>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="orders-prev-page">
              <ChevronLeft className="h-4 w-4" />Precedente
            </Button>
            <Button variant="outline" size="sm" disabled={page >= res.pages} onClick={() => setPage((p) => p + 1)} data-testid="orders-next-page">
              Successiva<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-mono-erp">{selected.order_number}</SheetTitle>
                <SheetDescription>{shortDate(selected.date)} · {selected.customer_name}</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="flex gap-2">
                  <Badge variant="outline" className={PAY_BADGE[selected.payment_status] || ""}>{PAY_LABEL[selected.payment_status] || selected.payment_status}</Badge>
                  <Badge variant="outline" className={FUL_BADGE[selected.fulfillment_status] || ""}>{FUL_LABEL[selected.fulfillment_status]}</Badge>
                </div>
                <div className="rounded-md border border-border/60">
                  <p className="border-b border-border/60 px-4 py-2 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Distinta articoli</p>
                  {!detail ? (
                    <div className="flex h-20 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                  ) : detail.items.map((it, idx) => (
                    <div key={`${it.sku}-${it.size}-${idx}`} className="flex items-center justify-between px-4 py-3 border-b border-border/40 last:border-0">
                      <div>
                        <p className="text-sm font-medium">{it.name}</p>
                        <p className="font-mono-erp text-[11px] text-muted-foreground">{it.sku} · {it.color} · Tg {it.size}</p>
                        <FitBadge fit={it.fit} size={it.size} testid={`order-item-fit-${idx}`} />
                      </div>
                      <div className="text-right">
                        <p className="text-sm">{it.quantity} pz{showMoney() && ` × ${eur(it.price)}`}</p>
                        {showMoney() && <p className="text-xs text-muted-foreground">{eur(it.quantity * it.price)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
                {showMoney() && <div className="flex items-center justify-between rounded-md bg-secondary px-4 py-3">
                  <span className="font-medium">Totale ordine</span>
                  <span className="font-display text-xl font-bold">{eur(selected.total)}</span>
                </div>}
                <p className="text-xs text-muted-foreground">Email cliente: {detail?.customer_email || "-"}</p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
